import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import {
  ArrowLeft,
  FloppyDisk,
  Trash,
  Copy,
  Play,
  Pause,
  Check,
  X,
  Plus,
  Tag
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import FileAttachments from '../../components/FileAttachments';

const statusOptions = [
  { value: 'planned', label: 'Planned' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' }
];

const ExperimentForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [protocols, setProtocols] = useState([]);
  const [tagInput, setTagInput] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    protocol_id: '',
    hypothesis: '',
    materials: [],
    status: 'planned',
    tags: [],
    results: '',
    conclusion: '',
    failure_reason: ''
  });

  useEffect(() => {
    loadProtocols();
    if (isEdit && id) {
      loadExperiment();
    }
  }, [id, isEdit]);

  const loadProtocols = async () => {
    try {
      const data = await api.getProtocols();
      setProtocols(data);
    } catch (error) {
      console.error('Failed to load protocols:', error);
    }
  };

  const loadExperiment = async () => {
    try {
      const data = await api.getExperiment(id);
      setFormData(data);
    } catch (error) {
      console.error('Failed to load experiment:', error);
      toast.error('Failed to load experiment');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const handleAddMaterial = () => {
    setFormData({ ...formData, materials: [...formData.materials, ''] });
  };

  const handleMaterialChange = (index, value) => {
    const newMaterials = [...formData.materials];
    newMaterials[index] = value;
    setFormData({ ...formData, materials: newMaterials });
  };

  const handleRemoveMaterial = (index) => {
    const newMaterials = formData.materials.filter((_, i) => i !== index);
    setFormData({ ...formData, materials: newMaterials });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit) {
        await api.updateExperiment(id, formData);
        toast.success('Experiment updated');
      } else {
        const result = await api.createExperiment(formData);
        toast.success('Experiment created');
        navigate(`/experiments/${result.id}`);
      }
    } catch (error) {
      toast.error('Failed to save experiment');
    } finally {
      setSaving(false);
    }
  };

  const handleClone = async () => {
    try {
      const cloned = await api.cloneExperiment(id);
      toast.success('Experiment cloned');
      navigate(`/experiments/${cloned.id}`);
    } catch (error) {
      toast.error('Failed to clone experiment');
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this experiment?')) {
      try {
        await api.deleteExperiment(id);
        toast.success('Experiment deleted');
        navigate('/experiments');
      } catch (error) {
        toast.error('Failed to delete experiment');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div data-testid="experiment-form-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Link to="/experiments" className="p-2 hover:bg-zinc-100 rounded-sm">
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="heading-display">{isEdit ? 'Edit Experiment' : 'New Experiment'}</h1>
            <p className="text-zinc-500 mt-1">{isEdit ? 'Update experiment details' : 'Create a new experiment'}</p>
          </div>
        </div>
        {isEdit && (
          <div className="flex gap-2">
            <Button variant="outline" className="btn-secondary flex items-center gap-2" onClick={handleClone}>
              <Copy size={16} /> Clone
            </Button>
            <Button variant="destructive" className="bg-red-600 text-white hover:bg-red-700 flex items-center gap-2" onClick={handleDelete}>
              <Trash size={16} /> Delete
            </Button>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            <div className="card-flat p-6 space-y-4">
              <div>
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Experiment title"
                  className="input-field w-full mt-1"
                  required
                  data-testid="experiment-title-input"
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe the experiment..."
                  className="input-field w-full mt-1 min-h-[100px]"
                  data-testid="experiment-description-input"
                />
              </div>

              <div>
                <Label htmlFor="hypothesis">Hypothesis</Label>
                <Textarea
                  id="hypothesis"
                  name="hypothesis"
                  value={formData.hypothesis}
                  onChange={handleChange}
                  placeholder="What do you expect to happen?"
                  className="input-field w-full mt-1"
                  data-testid="experiment-hypothesis-input"
                />
              </div>

              <div>
                <Label htmlFor="protocol_id">Protocol</Label>
                <select
                  id="protocol_id"
                  name="protocol_id"
                  value={formData.protocol_id || ''}
                  onChange={handleChange}
                  className="input-field w-full mt-1"
                  data-testid="experiment-protocol-select"
                >
                  <option value="">Select a protocol (optional)</option>
                  {protocols.map(p => (
                    <option key={p.id} value={p.id}>{p.title}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Materials */}
            <div className="card-flat p-6">
              <div className="flex items-center justify-between mb-4">
                <Label>Materials</Label>
                <Button type="button" variant="outline" size="sm" className="btn-secondary" onClick={handleAddMaterial}>
                  <Plus size={14} className="mr-1" /> Add Material
                </Button>
              </div>
              <div className="space-y-2">
                {formData.materials.map((material, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={material}
                      onChange={(e) => handleMaterialChange(idx, e.target.value)}
                      placeholder="Material name"
                      className="input-field flex-1"
                    />
                    <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveMaterial(idx)}>
                      <X size={16} />
                    </Button>
                  </div>
                ))}
                {formData.materials.length === 0 && (
                  <p className="text-sm text-zinc-500">No materials added yet</p>
                )}
              </div>
            </div>

            {/* Results (only in edit mode) */}
            {isEdit && (
              <div className="card-flat p-6 space-y-4">
                <h3 className="heading-card">Results & Conclusion</h3>
                <div>
                  <Label htmlFor="results">Results</Label>
                  <Textarea
                    id="results"
                    name="results"
                    value={formData.results}
                    onChange={handleChange}
                    placeholder="Document your results..."
                    className="input-field w-full mt-1 min-h-[100px]"
                    data-testid="experiment-results-input"
                  />
                </div>
                <div>
                  <Label htmlFor="conclusion">Conclusion</Label>
                  <Textarea
                    id="conclusion"
                    name="conclusion"
                    value={formData.conclusion}
                    onChange={handleChange}
                    placeholder="What did you learn?"
                    className="input-field w-full mt-1"
                    data-testid="experiment-conclusion-input"
                  />
                </div>
                {formData.status === 'failed' && (
                  <div>
                    <Label htmlFor="failure_reason">Failure Reason</Label>
                    <Textarea
                      id="failure_reason"
                      name="failure_reason"
                      value={formData.failure_reason}
                      onChange={handleChange}
                      placeholder="What went wrong?"
                      className="input-field w-full mt-1"
                      data-testid="experiment-failure-input"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <div className="card-flat p-6">
              <Label htmlFor="status">Status</Label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="input-field w-full mt-1"
                data-testid="experiment-status-select"
              >
                {statusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Tags */}
            <div className="card-flat p-6">
              <Label>Tags</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                  placeholder="Add tag"
                  className="input-field flex-1"
                />
                <Button type="button" variant="outline" onClick={handleAddTag}>
                  <Tag size={16} />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {formData.tags.map((tag, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-700 px-2 py-1 text-sm rounded-sm">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)} className="hover:text-red-400">
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Save Button */}
            <Button type="submit" className="btn-primary w-full" disabled={saving} data-testid="save-experiment-btn">
              {saving ? (
                <>
                  <div className="spinner w-4 h-4 mr-2" />
                  Saving...
                </>
              ) : (
                <>
                  <FloppyDisk size={16} className="mr-2" />
                  {isEdit ? 'Update Experiment' : 'Create Experiment'}
                </>
              )}
            </Button>
          </div>
        </div>
      </form>

      {isEdit && id && (
        <div className="card-flat p-5 mt-4">
          <FileAttachments entityType="experiments" entityId={id} />
        </div>
      )}
    </div>
  );
};

export default ExperimentForm;
